import {
  useBoundProp,
  useCardSession,
  useCardStatus,
  type ComponentFn,
} from '@littlebird/action-cards-view/react';
import { motion, useReducedMotion } from 'motion/react';
import { Children } from 'react';

import { Icons } from '@/assets/icons/icons';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';

import { useInDimmedObject } from '../../internals/objectContext';
import {
  GAP,
  sizingClass,
  STRUCK_TITLE_CLASS,
  TASK_ROW_CHECK_SIZE_CLASS,
  TEXT_STYLE,
  TONE_CLASS,
  WRAP_GAP,
} from '../../tokens';
import { TextArea } from '../TextArea';
import { TextInput } from '../TextInput';

import type { catalog } from '@littlebird/action-cards-view';

import './TaskRow.css';

type CheckTone = 'default' | 'p1' | 'p2' | 'p3' | 'p4';

// Todoist's own priority ring colours; P4 uses the app's muted foreground instead
const CHECK_TONE_COLOR: Partial<Record<CheckTone, string>> = {
  p1: '#D1453B',
  p2: '#EB8909',
  p3: '#246FE0',
};

function CheckGlyph({
  appearance,
  tone,
  checked,
}: {
  appearance: 'circle' | 'square';
  tone: CheckTone;
  checked: boolean;
}) {
  const reducedMotion = useReducedMotion();
  const Glyph =
    appearance === 'square'
      ? checked
        ? Icons.squareCheck
        : Icons.square
      : checked
        ? Icons.circleCheck
        : Icons.circle;
  const color = CHECK_TONE_COLOR[tone];
  const toneClass = color
    ? undefined
    : tone === 'p4'
      ? TONE_CLASS.muted
      : checked
        ? TONE_CLASS.done
        : TONE_CLASS.subtle;

  if (appearance === 'circle')
    return (
      <motion.svg
        aria-hidden="true"
        viewBox="0 0 18 18"
        className={cn(TASK_ROW_CHECK_SIZE_CLASS, toneClass)}
        style={color ? { color } : undefined}
      >
        <motion.circle
          cx="9"
          cy="9"
          r="8"
          stroke="currentColor"
          strokeWidth="1.5"
          initial={false}
          animate={{ fill: checked ? 'currentColor' : 'transparent' }}
          transition={{ duration: reducedMotion ? 0 : 0.12 }}
        />
        <motion.path
          d="M5 9l2.5 2.5L13 6"
          fill="none"
          className="stroke-background"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={false}
          animate={{ pathLength: checked ? 1 : 0, opacity: checked ? 1 : 0 }}
          transition={{ duration: reducedMotion ? 0 : 0.16 }}
        />
      </motion.svg>
    );
  return (
    <Glyph
      aria-hidden="true"
      className={cn(TASK_ROW_CHECK_SIZE_CLASS, toneClass)}
      style={color ? { color } : undefined}
    />
  );
}

// The check glyph dispatches `on.check` instead of writing `checked`: the row
// shows whatever state the host answers with, the way a Button's press never
// flips itself
export const TaskRow: ComponentFn<typeof catalog, 'TaskRow'> = ({
  props,
  bindings,
  emit,
  on,
  children,
}) => {
  const session = useCardSession();
  const reducedMotion = useReducedMotion();
  const status = useCardStatus();
  const [checked] = useBoundProp<boolean>(props.checked, bindings?.checked);

  const checkBound = on('check').bound;
  const checkEditable =
    checkBound &&
    (status === 'draft' || status === 'failed') &&
    props.editable !== false &&
    !session.readOnly;
  const done = props.done === true;
  // The object's own fade already dims the row, so a second fade would dim it twice
  const inDimmedObject = useInDimmedObject();
  const faded = !inDimmedObject && (done || props.dimmed === true);

  // A done task isn't edited: force the read form instead of relying on a
  // strike class reaching into TextInput's own <input>
  const titlePath = bindings?.title;
  const notesPath = bindings?.notes;
  // The check's box is one title line tall, so the row ends with the title and
  // whatever sits under it keeps the gap the document asks for
  const titleLine = cn('h-[1lh]', TEXT_STYLE[props.titleStyle ?? 'title']);

  return (
    <motion.div
      initial={false}
      animate={{ opacity: faded ? 0.55 : 1 }}
      transition={{ duration: reducedMotion ? 0 : 0.22 }}
      className={cn(
        'flex min-w-0 items-start',
        GAP.sm,
        sizingClass(props.sizing)
      )}
    >
      {/* The glyph sits on the title line, level with the card edge; the
          pseudo-element keeps the press target about 40px square */}
      {props.showCheck === false ? null : checkBound ? (
        <button
          type="button"
          aria-label={props.checkLabel}
          aria-pressed={checked === true}
          disabled={!checkEditable}
          className={cn(
            'relative flex shrink-0 items-center justify-center before:absolute before:-inset-x-3 before:-inset-y-2.5 disabled:cursor-default',
            titleLine
          )}
          onClick={() => emit('check')}
        >
          <CheckGlyph
            appearance={props.checkAppearance ?? 'circle'}
            tone={props.checkTone ?? 'default'}
            checked={checked === true}
          />
        </button>
      ) : (
        <span
          className={cn('flex shrink-0 items-center', titleLine)}
          aria-hidden="true"
        >
          <CheckGlyph
            appearance={props.checkAppearance ?? 'circle'}
            tone={props.checkTone ?? 'default'}
            checked={checked === true}
          />
        </span>
      )}
      <YStack className="min-w-0 flex-1 gap-[calc(2*var(--ac-u))]">
        {props.showTitle !== false ? (
          <span
            className={cn('min-w-0 card-task-title', done && 'card-task-done')}
          >
            {props.previousTitle ? (
              <s className={STRUCK_TITLE_CLASS}>{props.previousTitle}</s>
            ) : null}
            <TextInput
              props={{
                value: props.title,
                ariaLabel: 'Title',
                placeholder: props.placeholder,
                editable: done ? false : props.editable,
                appearance: 'inline',
                textStyle: props.titleStyle ?? 'title',
                tone: props.titleTone,
                weightClass: props.previousTitle ? 'font-semibold' : undefined,
                lines: props.titleLines,
                error: props.error,
                checks: props.titleChecks,
              }}
              bindings={
                titlePath !== undefined ? { value: titlePath } : undefined
              }
              emit={emit}
              on={on}
            />
          </span>
        ) : null}
        {props.notes?.trim() ||
        (notesPath !== undefined && session.focused === notesPath) ? (
          <span
            className={cn('min-w-0 card-task-title', done && 'card-task-done')}
          >
            <TextArea
              props={{
                value: props.notes ?? '',
                ariaLabel: 'Notes',
                placeholder: 'Notes',
                editable: done ? false : props.editable,
                appearance: 'inline',
                textStyle: 'note',
                checks: props.notesChecks,
                lines: props.notesLines,
              }}
              bindings={
                notesPath !== undefined ? { value: notesPath } : undefined
              }
              emit={emit}
              on={on}
            />
          </span>
        ) : null}
        {Children.count(children) > 0 ? (
          <XStack
            className={cn(
              'min-w-0 flex-wrap items-center pt-[calc(2*var(--ac-u))]',
              WRAP_GAP.sm
            )}
          >
            {children}
          </XStack>
        ) : null}
      </YStack>
    </motion.div>
  );
};
